import { nextTick, onBeforeUnmount, onMounted, watch, type Ref } from 'vue';
import { createDialogStack, trapTarget, type DialogEntry } from '~/utils/dialogStack';
import { lockScroll, unlockScroll } from '~/utils/scrollLock';

// Modal behaviour for the app's hand-built overlays: what a native <dialog>
// opened with showModal() would give for free. They stay hand-built because
// they are positioned under the fixed header and nest inside one another,
// and moving them to the top layer would restyle and restack all of them a
// week before the festival.
//
// While open, the overlay:
//   - takes focus (the element given as `initialFocus`, else the container,
//     which needs tabindex="-1")
//   - keeps Tab and Shift+Tab inside itself
//   - closes on Escape, through `onEscape`
//   - locks background scroll (iOS-safe, see utils/scrollLock)
// and on close hands focus back to whatever opened it.
//
// The template still carries the semantics: role="dialog" aria-modal="true"
// and an accessible name on the container.

type Entry = DialogEntry & { container: Ref<HTMLElement | null> };

const stack = createDialogStack<Entry>();
let nextId = 1;

const TABBABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

function tabbables(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(TABBABLE)).filter(
        // Skip anything not rendered — v-show panels, collapsed sections.
        (el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden',
    );
}

function onKeydown(event: KeyboardEvent) {
    const top = stack.top();
    if (!top) return;

    if (event.key === 'Escape') {
        if (event.defaultPrevented || !top.onEscape) return;
        event.preventDefault();
        top.onEscape();
        return;
    }

    if (event.key === 'Tab') {
        const container = top.container.value;
        if (!container) return;
        const target = trapTarget(tabbables(container), document.activeElement as HTMLElement | null, event.shiftKey);
        if (target) {
            event.preventDefault();
            target.focus();
        }
    }
}

export type UseDialogOptions = {
    // Open state for an overlay kept mounted (v-show). Omit for one rendered
    // with v-if, which is open for as long as it is mounted.
    active?: Ref<boolean> | (() => boolean);
    onEscape?: () => void;
    initialFocus?: Ref<HTMLElement | null>;
    lockScroll?: boolean;
};

export function useDialog(container: Ref<HTMLElement | null>, options: UseDialogOptions = {}) {
    const entry: Entry = { id: nextId++, container, onEscape: options.onEscape };
    const shouldLock = options.lockScroll !== false;
    let open = false;
    let returnTo: HTMLElement | null = null;

    function activate() {
        if (open) return;
        open = true;
        returnTo = document.activeElement as HTMLElement | null;
        if (stack.size === 0) document.addEventListener('keydown', onKeydown);
        stack.push(entry);
        if (shouldLock) lockScroll();
        nextTick(() => {
            const target = options.initialFocus?.value || container.value;
            target?.focus({ preventScroll: true });
        });
    }

    function deactivate() {
        if (!open) return;
        open = false;
        stack.remove(entry);
        if (stack.size === 0) document.removeEventListener('keydown', onKeydown);
        if (shouldLock) unlockScroll();
        // Only if the opener is still on the page — a route change or a
        // re-render may have replaced it, and focusing a detached node drops
        // focus to <body>, which is no better than leaving it alone.
        const target = returnTo;
        returnTo = null;
        if (target && target.isConnected && typeof target.focus === 'function') {
            target.focus({ preventScroll: true });
        }
    }

    onMounted(() => {
        if (options.active === undefined) activate();
        else watch(options.active, (on) => (on ? activate() : deactivate()), { immediate: true });
    });

    onBeforeUnmount(deactivate);

    return { activate, deactivate };
}
